import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader, EmptyState, LiveChip } from "@/components/shared";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ScrollText } from "lucide-react";

function ApiLogs() {
  const [logs, setLogs] = useState(null);
  const [level, setLevel] = useState("all");
  const load = useCallback(() => api.get("/logs", { params: level !== "all" ? { level } : {} }).then((r) => setLogs(r.data.items)).catch(() => setLogs([])), [level]);
  useEffect(() => { load(); }, [load]);
  // Telegram traffic produces events; refresh on them rather than on a timer.
  useLiveEvents(["bot.update", "job.updated", "bot.provisioned"], () => load());
  return (
    <div className="space-y-3">
      <Select value={level} onValueChange={setLevel}><SelectTrigger className="w-[160px]" data-testid="select-log-level"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">All levels</SelectItem><SelectItem value="INFO">Info</SelectItem><SelectItem value="ERROR">Error</SelectItem></SelectContent></Select>
      {!logs ? <Skeleton className="h-64 w-full" /> : logs.length === 0 ? (
        <div className="bg-card border border-border rounded-lg"><EmptyState icon={ScrollText} title="No API logs" desc="Telegram API calls (redacted) appear here." testId="empty-api-logs" /></div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden"><div className="divide-y divide-border/50 max-h-[560px] overflow-y-auto mono text-xs">
          {logs.map((l) => (<div key={l.id} className="px-4 py-2 flex items-center gap-3">
            <span className={l.ok ? "text-emerald-500" : "text-rose-500"}>{l.ok ? "OK " : "ERR"}</span>
            <span className="text-primary w-44 truncate">{l.method}</span><span className="text-muted-foreground w-16">{l.duration_ms}ms</span>
            <span className="text-muted-foreground flex-1 truncate">{l.error || ""}</span><span className="text-muted-foreground">{new Date(l.timestamp).toLocaleString()}</span>
          </div>))}
        </div></div>
      )}
    </div>
  );
}

function AuditLogs() {
  const [logs, setLogs] = useState(null);
  useEffect(() => { api.get("/audit").then((r) => setLogs(r.data.items)).catch(() => setLogs([])); }, []);
  if (!logs) return <Skeleton className="h-64 w-full" />;
  if (!logs.length) return <div className="bg-card border border-border rounded-lg"><EmptyState icon={ScrollText} title="No audit events" desc="User actions across the workspace appear here." testId="empty-audit" /></div>;
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden"><div className="divide-y divide-border/50 max-h-[560px] overflow-y-auto text-sm">
      {logs.map((a) => (<div key={a.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="min-w-0"><span className="mono text-primary text-xs">{a.action}</span> <span className="text-xs text-muted-foreground">by {a.user_email}</span></div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(a.timestamp).toLocaleString()}</span>
      </div>))}
    </div></div>
  );
}

export default function Logs() {
  return (
    <div className="space-y-5">
      <PageHeader title="Logs" desc="Structured API logs and audit trail. Secrets &amp; tokens are redacted." actions={<LiveChip />} testId="logs-header" />
      <Tabs defaultValue="api">
        <TabsList><TabsTrigger value="api" data-testid="tab-api-logs">API logs</TabsTrigger><TabsTrigger value="audit" data-testid="tab-audit-logs">Audit trail</TabsTrigger></TabsList>
        <TabsContent value="api" className="mt-4"><ApiLogs /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditLogs /></TabsContent>
      </Tabs>
    </div>
  );
}
